/* ModuleUtilizationChart auto-generated by npm run generator */
import { Bar } from "@nivo/bar"
import { clampNumber, Client, ModuleStatus } from "@qlarity/common"
import ChartCard from "components/atoms/ChartCard"
import { observer } from "mobx-react"
import React, { CSSProperties } from "react"
import Store from "store/Store"
import { defaultTheme } from "style"
import { defaultNivo } from "style/nivoTheme"

interface ModuleUtilizationChartProps {
    className?: string
    style?: CSSProperties
    children?: React.ReactNode
    clients: Client[] | undefined
    type: ModuleStatus
}
enum Label {
    ONCE = "1 completion",
    LESS_THAN_FIVE = "< 5 completions",
    MORE_THAN_FIVE = "> 5 completions",
    ONCE_COLOR = `1 completionColor`,
    LESS_THAN_COLOR = "< 5 completionsColor",
    MORE_THAN_COLOR = "> 5 completionsColor",
}

interface ModUtilData {
    name?: string
    type?: string
    [Label.ONCE]?: number
    [Label.LESS_THAN_FIVE]?: number
    [Label.MORE_THAN_FIVE]?: number
    [Label.ONCE_COLOR]: string
    [Label.LESS_THAN_COLOR]: string
    [Label.MORE_THAN_COLOR]: string
    max: number
}
const ModuleUtilizationChart: React.FC<ModuleUtilizationChartProps> = ({ clients, type }) => {
    const keys = [Label.ONCE, Label.LESS_THAN_FIVE, Label.MORE_THAN_FIVE]
    const preparedData = React.useMemo(() => {
        const data = Store.clientStore.modules?.map((mod) => {
            const modCompletions = clients?.reduce(
                (total: ModUtilData, client) => {
                    total.name = mod.moduleName.split(".")[0]
                    const numCompletions = type === ModuleStatus.TRAINING ? client.progress?.[mod.id]?.numTrainings : client.progress?.[mod.id]?.numPractices
                    if (!numCompletions) {
                        return total
                    }
                    if (numCompletions === 1) {
                        total[Label.ONCE]! += 1
                    } else if (numCompletions && numCompletions < 5) {
                        total[Label.LESS_THAN_FIVE]! += 1
                    } else if (numCompletions && numCompletions >= 5) {
                        total[Label.MORE_THAN_FIVE]! += 1
                    }
                    total.max = Math.max(total[Label.ONCE] ?? 0, total[Label.LESS_THAN_FIVE] ?? 0, total[Label.MORE_THAN_FIVE] ?? 0)

                    return total
                },
                {
                    [Label.ONCE]: 0,
                    [Label.LESS_THAN_FIVE]: 0,
                    [Label.MORE_THAN_FIVE]: 0,
                    max: 1,
                    [Label.ONCE_COLOR]: defaultTheme.palette.info.main,
                    [Label.LESS_THAN_COLOR]: defaultTheme.palette.info.dark,
                    [Label.MORE_THAN_COLOR]: defaultTheme.palette.info.light,
                }
            )

            return (
                modCompletions ?? {
                    [Label.ONCE]: 0,
                    [Label.LESS_THAN_FIVE]: 0,
                    [Label.MORE_THAN_FIVE]: 0,
                    max: 1,
                }
            )
        })
        return data ?? []
    }, [clients, type])
    const maxY = preparedData.reduce((max, curr) => (max >= curr.max ? max : curr.max), 1)
    const numTicks = clampNumber(maxY, 1, 5)

    return (
        <ChartCard title={`Module Utilization: ${type}`} subtitle="Module Number">
            <Bar
                theme={defaultNivo}
                width={600}
                height={300}
                data={clients ? preparedData : []}
                keys={keys}
                indexBy={"name"}
                groupMode={"stacked"}
                axisLeft={{ tickValues: numTicks, legend: "Active Users", legendOffset: -30, legendPosition: "middle" }}
                // @ts-ignore
                gridYValues={numTicks}
                colors={({ id, data }) => data[`${id}Color`]}
                margin={{ bottom: 40, top: 32, left: 40, right: 132 }}
                labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                legends={[
                    {
                        dataFrom: "keys",
                        anchor: "bottom-right",
                        direction: "column",
                        justify: false,
                        translateX: 120,
                        translateY: 0,
                        itemsSpacing: 2,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: "left-to-right",
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                            {
                                on: "hover",
                                style: {
                                    itemOpacity: 1,
                                },
                            },
                        ],
                    },
                ]}
                isInteractive={false}
            />
        </ChartCard>
    )
}

export default observer(ModuleUtilizationChart)
